/**
 * Загрузка раздела «Экология» (ecology-sites): обзор площадок и строк по скоупу (клиент или команда
 * по `cid`) плюс справочник и настройки сроков. Все мутации раздела отвечают свежим обзором —
 * страницы кладут его через `setOverview`, повторной загрузки не нужно.
 *
 * `api` должен быть стабильным (модульная константа или `useMemo`) — иначе загрузка повторится на
 * каждом рендере.
 */
import { useEffect, useState } from 'react'
import {
  ecologyApi,
  type EcoCatalogItem,
  type EcoOverview,
  type EcoScopeApi,
  type EcoSettings,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'

export interface EcoData {
  overview: EcoOverview | null
  catalog: EcoCatalogItem[]
  settings: EcoSettings | null
  loading: boolean
  error: string | null
  setOverview: (overview: EcoOverview) => void
}

export function useEcoData(api: EcoScopeApi): EcoData {
  const [overview, setOverview] = useState<EcoOverview | null>(null)
  const [catalog, setCatalog] = useState<EcoCatalogItem[]>([])
  const [settings, setSettings] = useState<EcoSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    Promise.all([
      api.overview(controller.signal),
      ecologyApi.listCatalog(controller.signal),
      ecologyApi.getSettings(controller.signal),
    ])
      .then(([o, c, s]) => {
        if (controller.signal.aborted) return
        setOverview(o)
        setCatalog(c)
        setSettings(s)
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setError(getErrorMessage(err, 'Не удалось загрузить раздел «Экология»'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [api])

  return { overview, catalog, settings, loading, error, setOverview }
}
